import React, {FC, Fragment, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {KeyboardActionsEnum, KeyboardShortcut} from '../../../../core/src/helpers/browser/keyboardHelpers';
import {isMacOS} from '../../../../core/src/helpers/environment/platformHelpers';
import {intersperse} from '../../../../core/src/helpers/utilities/arrayHelpers';
import {useKeyboardContext} from './keyboardContext';
import {keyRenderers, knownKeyWithSymbols, splitAndFixKeys} from './keyboardShortcutFormatterHelpers';

/*
 * Props.
 */

export interface TextKeyboardShortcutsFormatterProps {
  /** The keyboard action to display. */
  action: KeyboardActionsEnum;

  /** Whether to show all shortcuts. */
  showAllShortcuts?: boolean;
}

/*
 * Style.
 */

const StyledKeySpan = styled.span<{$useTitleCase: boolean; $isSymbol: boolean; $needsMinWidth: boolean}>`
  text-align: center;
  ${(p) =>
    !p.$isSymbol &&
    css`
      text-align: left;
      padding-left: 0.2ch;
    `};
  ${(p) =>
    p.$useTitleCase &&
    css`
      text-transform: capitalize;
    `};
  ${(p) =>
    p.$needsMinWidth &&
    css`
      min-width: 1.6ch;
    `};
`;

/*
 * Component.
 */

export const TextKeyboardShortcutsFormatter: FC<TextKeyboardShortcutsFormatterProps> = (props) => {
  const {mapping} = useKeyboardContext();

  /** Render the shortcuts for the specified action and mapping. */
  const renderedShortcuts = mapping[props.action].map(renderKeyboardShortcut);

  if (!props.showAllShortcuts) {
    return <>{renderedShortcuts[0]}</>;
  }

  // Render each shortcut, then separate them by ",".
  return <>{intersperse<ReactNode>(renderedShortcuts, ', ')}</>;
};

/** Render a single shortcut. */
export function renderKeyboardShortcut(shortcut: KeyboardShortcut, index: number) {
  const fixedKeys = shortcut.sequence.split(' ').flatMap((key) => splitAndFixKeys(key));

  const needsSeparator = !isMacOS();
  const shouldUseSymbol = isMacOS();

  // Find the correct translation for each.
  /* eslint react/no-array-index-key: 0 */
  const translatedKeys = fixedKeys.map((key, i, array) => {
    const renderer = keyRenderers[key];
    const showSeparator = needsSeparator && i < array.length - 1;

    return (
      <Fragment key={i}>
        <StyledKeySpan
          $needsMinWidth={!needsSeparator}
          $useTitleCase={!shouldUseSymbol}
          $isSymbol={knownKeyWithSymbols.includes(key)}
        >
          {renderer ? renderer(shouldUseSymbol) : maybeUpperCase(key)}
        </StyledKeySpan>
        {showSeparator && <span>+</span>}
      </Fragment>
    );
  });

  return <Fragment key={index}>{translatedKeys}</Fragment>;
}

function maybeUpperCase(key: string) {
  if (key.match(/^[a-z]$/)) {
    return key.toUpperCase();
  }
  return key;
}
